import { QueryClient } from '@tanstack/vue-query';
import { isAxiosError } from '@/helpers/api';

// One minute keeps a second consumer mounting right after the first (tabs,
// modals, the many components sharing one order) on the cached response.
// Resource-specific queries override this where the data is expensive or
// derived (e.g. the order-files list, position material summaries).
export const DEFAULT_STALE_TIME_MS = 60_000;
// Long enough to switch between order variants and come back without a refetch.
export const DEFAULT_GC_TIME_MS = 10 * 60_000;
export const MAX_QUERY_RETRIES = 1;
// Try again errors:
// the API rate-limits with 429, and a proxy in front of it can answer 408.
const RETRYABLE_CLIENT_ERRORS = [408, 429];

// Do not retry on unretriable server errors.
export function shouldRetryQuery(failureCount: number, error: unknown) {
  if (failureCount >= MAX_QUERY_RETRIES) return false;

  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status === undefined) return true;
  if (RETRYABLE_CLIENT_ERRORS.includes(status)) return true;

  return status < 400 || status >= 500;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: DEFAULT_STALE_TIME_MS,
      gcTime: DEFAULT_GC_TIME_MS,
      // Big orders carry hundreds of files and thousands of parts; a refetch of
      // everything on every window focus is real network and memory cost.
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery,
    },
    mutations: {
      retry: 0,
    },
  },
});
